import { type JSX, type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';

import type { Bereichsgruppe } from './Rahmen';
import { Schliesskreuz } from './Schliesskreuz';
import { Sinnbild } from './Sinnbild';

/**
 * DIE MENÜZEILE AUF DEM TELEFON (Auftrag CRM-106, ÄA-63).
 *
 * Unter 600 px trägt der Kopf nur noch einen Menü-Knopf; er öffnet diese
 * Schublade von links. Darin dieselben Gruppen in derselben Folge wie in der
 * Menüzeile, jede als Akkordeon mit Zeilen von 44 px (WCAG 2.5.5). Der Name
 * einer Gruppe öffnet ihre Punkte; ihr erster Eintrag „Übersicht" führt auf
 * die Bereichsseite — auf dem Desktop ist das der Name selbst (Auftrag #25),
 * hier wäre ein Tippen, das zugleich aufklappt und wegführt, ein Fehlgriff.
 *
 * Der Fokus geht wie im Dialog (NFA-14, WCAG 2.4.3, 2.1.2): beim Öffnen auf
 * die Gruppe, in der man steht, Tabulator im Kreis, beim Schließen zurück
 * auf den Menü-Knopf. Esc, das Kreuz und ein Tippen daneben schließen.
 */

const BEDIENBAR = 'a[href], button:not([disabled])';

export function Schublade({
  gruppen,
  gruppe,
  bereich,
  beiGruppe,
  beiBereich,
  beiSchliessen,
}: {
  readonly gruppen: readonly Bereichsgruppe[];
  readonly gruppe: string | null;
  readonly bereich: string | null;
  readonly beiGruppe: (kennung: string) => void;
  readonly beiBereich: (kennung: string) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const flaeche = useRef<HTMLDivElement>(null);
  const titelId = useId();
  /* Die Gruppe, in der man steht, ist offen — der kürzeste Weg zum Nachbarpunkt. */
  const [offen, setzeOffen] = useState<string | null>(gruppe);

  useEffect(() => {
    const herkunft = document.activeElement as HTMLElement | null;
    const ziel =
      flaeche.current?.querySelector<HTMLElement>('[aria-current]') ??
      flaeche.current?.querySelector<HTMLElement>('.schublade__gruppe');
    ziel?.focus();
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = vorher;
      herkunft?.focus();
    };
  }, []);

  const beiTaste = (ereignis: KeyboardEvent<HTMLDivElement>): void => {
    if (ereignis.key === 'Escape') {
      ereignis.preventDefault();
      beiSchliessen();
      return;
    }
    if (ereignis.key !== 'Tab') return;
    const alle = [...(flaeche.current?.querySelectorAll<HTMLElement>(BEDIENBAR) ?? [])];
    const erstes = alle[0];
    const letztes = alle[alle.length - 1];
    if (erstes === undefined || letztes === undefined) return;
    if (ereignis.shiftKey && document.activeElement === erstes) {
      ereignis.preventDefault();
      letztes.focus();
    } else if (!ereignis.shiftKey && document.activeElement === letztes) {
      ereignis.preventDefault();
      erstes.focus();
    }
  };

  const gehe = (wahl: () => void): void => {
    wahl();
    beiSchliessen();
  };

  return (
    <div
      className="schublade"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) beiSchliessen();
      }}
    >
      <div
        className="schublade__flaeche"
        ref={flaeche}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        id="schublade"
        onKeyDown={beiTaste}
      >
        <div className="schublade__kopf">
          <h2 className="schublade__titel" id={titelId}>
            Menü
          </h2>
          <Schliesskreuz beschriftung="Menü schließen" beiKlick={beiSchliessen} />
        </div>
        <nav aria-label="Bereiche">
          <ul className="schublade__gruppen">
            {gruppen.map((g) => {
              const hier = g.kennung === gruppe;
              const einziger = g.bereiche.length === 1 ? g.bereiche[0] : undefined;
              if (einziger !== undefined) {
                return (
                  <li key={g.kennung}>
                    <button
                      type="button"
                      className="schublade__gruppe"
                      aria-current={hier ? 'page' : undefined}
                      onClick={() => gehe(() => beiGruppe(g.kennung))}
                    >
                      {g.beschriftung}
                    </button>
                  </li>
                );
              }
              const auf = offen === g.kennung;
              const listeId = `schublade-${g.kennung}`;
              return (
                <li key={g.kennung}>
                  <button
                    type="button"
                    className={`schublade__gruppe${hier ? ' schublade__gruppe--hier' : ''}`}
                    aria-expanded={auf}
                    aria-controls={listeId}
                    onClick={() => setzeOffen(auf ? null : g.kennung)}
                  >
                    <span>{g.beschriftung}</span>
                    <Sinnbild name="caret-down" gewicht="light" klasse="schublade__pfeil" />
                  </button>
                  {auf && (
                    <ul className="schublade__punkte" id={listeId}>
                      <li>
                        <a
                          className="schublade__punkt"
                          href={`/${g.kennung}`}
                          aria-current={hier && bereich === null ? 'page' : undefined}
                          onClick={(e) => {
                            e.preventDefault();
                            gehe(() => beiGruppe(g.kennung));
                          }}
                        >
                          <Sinnbild name="squares-four" gewicht="light" />
                          Übersicht
                        </a>
                      </li>
                      {g.bereiche.map((b) => (
                        <li key={b.kennung}>
                          <button
                            type="button"
                            className="schublade__punkt"
                            aria-current={hier && b.kennung === bereich ? 'page' : undefined}
                            onClick={() => gehe(() => beiBereich(b.kennung))}
                          >
                            <Sinnbild name={b.sinnbild} gewicht="light" />
                            {b.beschriftung}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}
